// src/utils/variableRefUtils.js – 2025-06-14 (rev‑b)
// -----------------------------------------------------------------------------
// • tooltipSpan(asyncOpts) unchanged – still opts‑object, .var-ref / .var-missing
// • TAG_RX branch now builds the **same extended tooltip** as <a> variables
//   (ID + current EN plain‑text) so both token types behave consistently.
//
import { debugLog, isImportExport } from "./index";

/* ─────────────────────────────── regex helpers ────────────────────────────── */
const TAG_RX = /\{entry:(\d+)(?:@\d+)?:plainText}/g;
const LINK_RX = /<a[^>]*href="\{entry:(\d+)@(\d+):url\|\|([^"]+)}"[^>]*>([\s\S]*?)<\/a>/g;

/* ───────────────────────────────────────────────────────────────────────────── */
export function applyVariables(str, valueMap = {}, opts = {}) {
  if (typeof str !== "string" || !str) return str;

  const importExport = opts.importExport ?? isImportExport();
  const wrap = Boolean(opts.wrap);
  const preview = wrap;

  return (
    str
      /* ── 1) raw‑entry <a> links ─────────────────────────────── */
      .replace(LINK_RX, (m, id, lang, _fallback, originalText) => {
        /* ── NEW: only treat as a *variable* link when the URL points to
         *        “…/variables/<id>” (with or without “/digiguide/”).       */
        const isVariableUrl = /\/(?:digiguide\/)?variables(-scoped)?\/\d+/i.test(
          _fallback
        );
        if (!isVariableUrl) {
          debugLog?.("[Variables] skipped non-variable link:", _fallback);
          return m; // leave anchor untouched → glossary
        }

        const globalVal = valueMap[id] ?? "";
        const inheritVal = opts.inheritedMap?.[id] ?? "";
        const inheritId = opts.inheritedIdMap?.[id] ?? "";
        const text = inheritVal || globalVal || originalText || "";
        const missing = !inheritVal && !globalVal;

        /* export preview keeps <a> */
        if (importExport && wrap) {
          if (missing) {
            return tooltipSpan({
              id,
              inner: originalText || m,
              tooltip: "Variable not found",
              missing: true,
            });
          }
          return (
            `<a href="variables-scoped/#entry:${id}@${opts.currentSiteId ||
              lang}"` +
            ` data-var-id="${id}"` +
            ` globalVariablePlainText="${escapeAttr(globalVal)}"` +
            ` primarySitePlainText="${escapeAttr(globalVal)}"` +
            ` variableToInheritId="${inheritId}">` +
            `${escapeHtml(text)}` +
            `</a>`
          );
        }

        /* normal preview as span */
        if (preview) {
          const tip = missing
            ? "Variable not found"
            : `ID: ${id}<br>Original: \"${escapeHtml(
                originalText
              )}\"<br>Variable: \"${escapeHtml(text)}\"`;
          return tooltipSpan({ id, inner: text, tooltip: tip, missing });
        }

        return escapeHtml(text);
      })

      /* ── 2) {entry:ID:plainText} tokens ─────────────────────── */
      .replace(TAG_RX, (m, id) => {
        const val = valueMap[id] ?? "";
        const missing = !val;

        /* shared tooltip body */
        const tip = missing
          ? "Variable not found"
          : `ID: ${id}<br>Variable: \"${escapeHtml(val)}\"`;

        if (importExport && wrap) {
          return tooltipSpan({
            id,
            inner: missing ? m : val,
            tooltip: tip,
            missing,
          });
        }
        if (preview) {
          return tooltipSpan({
            id,
            inner: missing ? m : val,
            tooltip: tip,
            missing,
          });
        }
        return escapeHtml(val);
      })
  );
}

/* ───────────────────────────── helper: tooltip wrapper ─────────────────────── */
function tooltipSpan({ id, inner, tooltip, missing = false }) {
  const cls = `${missing ? "var-missing" : "var-ref"} tooltip`;
  return (
    `<span class="${cls}" data-var-id="${id}">` +
    `${escapeHtml(inner)}` +
    `<span class="tooltiptext">${tooltip}</span>` +
    `</span>`
  );
}

/* simple escapers */
function escapeAttr(s) {
  return String(s).replace(/"/g, "&quot;");
}
function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export default applyVariables;
